import { useState, type ReactNode } from 'react'
import {
    revokeIngestToken,
    setAlertStatus,
    updateNotifySettings,
} from '~/api/monitoring'
import type { AdminDashboardAlert, AdminDashboardDetail } from '~/api/types'
import { ConfirmDialog } from '~/components/ConfirmDialog'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '~/components/ui/table'
import { useToast } from '~/components/ui/toast'
import { Toggle } from '~/components/ui/toggle'
import { formatDateTime } from '~/lib/utils'
import { useAuthStore } from '~/stores/auth-store'

// 대시보드 상세 보강 블록(패널/페이지 공용). 인제스트 건강도·통지 설정·지표·소스 매핑.
// 4요소: 식별자(필드/소스 유형)·누가(통지 대상)·무엇을(값/비중)·언제(적재/최근 시각).

const SOURCE_LABELS: Record<string, string> = {
    manual: '수기',
    excel: '엑셀',
    api: 'API',
    image: '이미지',
}

/** 라벨/값 정의쌍. 값이 없으면 '—'를 넣는다(상위에서 처리). */
export function Field({ label, value }: { label: string; value: ReactNode }) {
    return (
        <div>
            <dt className='krds-label'>{label}</dt>
            <dd className='text-foreground mt-0.5'>{value}</dd>
        </div>
    )
}

/** 인제스트 건강도: 마지막 적재 · 소스 비중 배지 · 측정수. */
export function IngestHealth({ detail }: { detail: AdminDashboardDetail }) {
    const entries = detail.sourceBreakdown
        ? Object.entries(detail.sourceBreakdown).filter(([, v]) => v > 0)
        : []
    return (
        <dl className='grid grid-cols-2 gap-x-4 gap-y-2 text-sm'>
            <Field
                label='마지막 적재'
                value={formatDateTime(detail.lastIngestAt)}
            />
            <Field
                label='측정수'
                value={<span className='tnum'>{detail.readingsCount}</span>}
            />
            <div className='col-span-2'>
                <dt className='krds-label'>소스 비중</dt>
                <dd className='mt-1 flex flex-wrap gap-1'>
                    {entries.length === 0 ? (
                        <span className='text-muted-foreground'>—</span>
                    ) : (
                        entries.map(([k, v]) => (
                            <Badge key={k} tone='neutral'>
                                {SOURCE_LABELS[k] ?? k}{' '}
                                <span className='tnum'>{v}</span>
                            </Badge>
                        ))
                    )}
                </dd>
            </div>
        </dl>
    )
}

// 처리 가능한 알림 상태 집합(읽기 표기 변형 포함). resolved/closed면 더 처리할 게 없다.
const ALERT_UNREAD = new Set(['unread', 'open'])
const ALERT_RESOLVED = new Set(['resolved', 'closed'])

/** 통지 설정: 이메일 · 이메일 on-off · 토큰 발급/폐기.
 *  읽기 모드(배지) ↔ 편집 모드(폼) 토글. 변경 후 onChanged로 부모가 상세를 새로고침한다. */
export function NotifySettings({
    detail,
    onChanged,
}: {
    detail: AdminDashboardDetail
    /** 저장/폐기 성공 후 호출 — 부모가 상세를 refetch한다. */
    onChanged?: () => void | Promise<void>
}) {
    const toast = useToast()
    // 토큰 폐기는 superadmin에게만 노출(서버도 게이트). 역할이 다르면 버튼을 숨긴다.
    const isSuperadmin = useAuthStore((s) => s.adminRole) === 'superadmin'
    const [editing, setEditing] = useState(false)
    const [saving, setSaving] = useState(false)
    const [revoking, setRevoking] = useState(false)
    const [confirmRevoke, setConfirmRevoke] = useState(false)
    const [form, setForm] = useState({
        notifyEmail: detail.notifyEmail ?? '',
        notifyEmailOn: detail.notifyEmailOn ?? false,
    })

    function startEdit() {
        setForm({
            notifyEmail: detail.notifyEmail ?? '',
            notifyEmailOn: detail.notifyEmailOn ?? false,
        })
        setEditing(true)
    }

    async function save() {
        setSaving(true)
        try {
            await updateNotifySettings(detail.id, {
                notifyEmail: form.notifyEmail.trim() || null,
                notifyEmailOn: form.notifyEmailOn,
            })
            toast.success('통지 설정을 저장했어요.')
            setEditing(false)
            await onChanged?.()
        } catch (e) {
            toast.error((e as Error).message)
        } finally {
            setSaving(false)
        }
    }

    async function revoke() {
        setRevoking(true)
        try {
            await revokeIngestToken(detail.id)
            setConfirmRevoke(false)
            toast.success(
                '적재 토큰을 폐기했어요. 재발급 전까지 장비 적재가 중단돼요.'
            )
            await onChanged?.()
        } catch (e) {
            toast.error((e as Error).message)
        } finally {
            setRevoking(false)
        }
    }

    if (editing) {
        return (
            <div className='space-y-3 text-sm'>
                <div>
                    <Label htmlFor='notify-email'>이메일</Label>
                    <Input
                        id='notify-email'
                        type='email'
                        size='sm'
                        className='mt-1'
                        placeholder='alert@example.com'
                        value={form.notifyEmail}
                        onChange={(e) =>
                            setForm((f) => ({
                                ...f,
                                notifyEmail: e.target.value,
                            }))
                        }
                    />
                </div>
                <div className='flex flex-wrap items-center gap-4'>
                    <Toggle
                        checked={form.notifyEmailOn}
                        onCheckedChange={(v) =>
                            setForm((f) => ({ ...f, notifyEmailOn: v }))
                        }
                        label='이메일 알림'
                    />
                </div>
                <div className='flex justify-end gap-2'>
                    <Button
                        variant='outline'
                        size='sm'
                        disabled={saving}
                        onClick={() => setEditing(false)}>
                        취소
                    </Button>
                    <Button size='sm' disabled={saving} onClick={save}>
                        저장
                    </Button>
                </div>
            </div>
        )
    }

    return (
        <>
            <dl className='grid grid-cols-2 gap-x-4 gap-y-2 text-sm'>
                <Field label='이메일' value={detail.notifyEmail ?? '—'} />
                <div className='col-span-2 flex flex-wrap items-center gap-1.5'>
                    <Badge tone={detail.notifyEmailOn ? 'success' : 'neutral'}>
                        이메일 {detail.notifyEmailOn ? 'ON' : 'OFF'}
                    </Badge>
                    {detail.tokenIssued ? (
                        <Badge tone='info'>토큰 발급됨</Badge>
                    ) : null}
                </div>
                <div className='col-span-2 flex flex-wrap items-center gap-2'>
                    <Button variant='outline' size='sm' onClick={startEdit}>
                        편집
                    </Button>
                    {detail.tokenIssued && isSuperadmin ? (
                        <Button
                            variant='outline'
                            size='sm'
                            disabled={revoking}
                            onClick={() => setConfirmRevoke(true)}>
                            토큰 폐기
                        </Button>
                    ) : null}
                </div>
            </dl>
            <ConfirmDialog
                open={confirmRevoke}
                title='적재 토큰을 폐기할까요?'
                description='재발급 전까지 장비 적재가 중단됩니다. 이 작업은 되돌릴 수 없어요.'
                confirmLabel='토큰 폐기'
                danger
                onConfirm={revoke}
                onCancel={() => setConfirmRevoke(false)}
            />
        </>
    )
}

/** 알림 처리 버튼: unread→[확인][해결], ack→[해결], resolved→"해결됨".
 *  처리 후 onChanged로 부모가 상세를 새로고침해 상태/배지를 갱신한다. */
export function AlertActions({
    dashboardId,
    alert,
    onChanged,
}: {
    dashboardId: string
    alert: AdminDashboardAlert
    onChanged?: () => void | Promise<void>
}) {
    const toast = useToast()
    const [busy, setBusy] = useState(false)
    const status = alert.status ?? ''

    async function apply(next: 'ack' | 'resolved') {
        setBusy(true)
        try {
            await setAlertStatus(dashboardId, alert.id, next)
            toast.success(
                next === 'ack'
                    ? '알림을 확인 처리했어요.'
                    : '알림을 해결 처리했어요.'
            )
            await onChanged?.()
        } catch (e) {
            toast.error((e as Error).message)
        } finally {
            setBusy(false)
        }
    }

    if (ALERT_RESOLVED.has(status)) {
        return (
            <span className='text-muted-foreground text-[12px] whitespace-nowrap'>
                해결됨
            </span>
        )
    }
    return (
        <div className='flex items-center justify-end gap-1.5'>
            {ALERT_UNREAD.has(status) ? (
                <Button
                    variant='outline'
                    size='xs'
                    disabled={busy}
                    onClick={() => apply('ack')}>
                    확인
                </Button>
            ) : null}
            <Button
                variant='outline'
                size='xs'
                disabled={busy}
                onClick={() => apply('resolved')}>
                해결
            </Button>
        </div>
    )
}

/** 지표 목록: 이름 · 단위 · 최근값(@시각) · 건수. */
export function IndicatorTable({ detail }: { detail: AdminDashboardDetail }) {
    const rows = detail.indicators ?? []
    if (rows.length === 0)
        return <p className='text-muted-foreground text-sm'>지표가 없어요.</p>
    return (
        <Table>
            <TableHeader>
                <TableRow>
                    <TableHead>지표</TableHead>
                    <TableHead>단위</TableHead>
                    <TableHead>최근값</TableHead>
                    <TableHead>건수</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {rows.map((r) => (
                    <TableRow key={r.fieldId}>
                        <TableCell className='font-medium'>
                            {r.nameKo}
                        </TableCell>
                        <TableCell>{r.unit ?? '—'}</TableCell>
                        <TableCell>
                            {r.lastValue != null ? (
                                <>
                                    <span className='tnum'>{r.lastValue}</span>
                                    <span className='text-muted-foreground'>
                                        {' @ '}
                                        {formatDateTime(r.lastAt)}
                                    </span>
                                </>
                            ) : (
                                '—'
                            )}
                        </TableCell>
                        <TableCell className='tnum'>{r.count}</TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    )
}

/** 소스 매핑: 소스 유형 · 필드수 배지. */
export function SourceMappings({ detail }: { detail: AdminDashboardDetail }) {
    const rows = detail.sourceMappings ?? []
    if (rows.length === 0)
        return <span className='text-muted-foreground text-sm'>—</span>
    return (
        <div className='flex flex-wrap gap-1.5'>
            {rows.map((m) => (
                <Badge key={m.sourceType} tone='neutral'>
                    {SOURCE_LABELS[m.sourceType] ?? m.sourceType}{' '}
                    <span className='tnum'>{m.fieldCount}</span>
                </Badge>
            ))}
        </div>
    )
}
